import styled from '@emotion/styled';
import { ButtonProps, message, Spin, Tooltip } from 'antd';
import Button from 'antd/es/button';
import Popconfirm from 'antd/es/popconfirm';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useCallback, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { CancelOrder, handleBuyerCancellation } from 'services/api/refund';
import TikTokRefuse from 'components/Refund/TikTokRefuse';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { cx } from '@emotion/css';

const PopConfrimContent = styled.div`
  width: 260px;
`;

type BasicButtonProps = Omit<ButtonProps, 'onClick'>;
export interface CancelApprovalBtnsProps {
  platform: General.CaselessPlatformTunnel;
  region?: General.UpperCaseRegion;
  internalStoreId: string;
  externalStoreId: CancelOrder['externalStoreId'];
  orderId: CancelOrder['orderId'];
  onUpdate?: () => void;
  buttonProps?: BasicButtonProps | [BasicButtonProps | undefined, BasicButtonProps | undefined];
  operate?: Conversation.Trade.IReturnOperationType[];
}

const CancelApprovalBtns = (props: CancelApprovalBtnsProps) => {
  const { t } = useTranslate();

  const sysLang = useSystemLanguageValue();
  const [loading, setLoading] = useState(false);
  const {
    platform,
    region,
    internalStoreId,
    externalStoreId,
    orderId,
    onUpdate,
    buttonProps,
    operate,
  } = props;

  const [leftBtnProp, rightBtnProp] = useMemo(() => {
    return Array.isArray(buttonProps) ? buttonProps : [buttonProps, buttonProps];
  }, [buttonProps]);

  const handleClick = useCallback(
    (isAccept: boolean) => {
      setLoading(true);
      handleBuyerCancellation({
        externalStoreId,
        orderId,
        platform,
        isAccept,
      })
        .then(() => {
          onUpdate && onUpdate();
          message.success(t('操作成功'));
        })
        .catch((err) => {
          if (err?.response?.data?.code === 1029) {
            // 没有权限
            try {
              const json = JSON.parse(err.response.data.msg);
              const msg = json[sysLang];
              message.error(msg);
            } catch (error) {
              message.error(t('translation.操作失败，请稍后再试'));
            }
          } else if (err?.response?.data?.code === 1028) {
            // 已由其他人处理
            message.error(t('errorMsg.操作失败，已被其他人处理'));
            onUpdate && onUpdate();
          } else {
            message.error(t('translation.操作失败，请稍后再试'));
          }
        })
        .finally(() => setLoading(false));
    },
    [externalStoreId, onUpdate, orderId, platform, sysLang, t],
  );

  let confirmUI;
  let rejectUI;
  if (platform === 'SHOPEE') {
    const disabled = !operate?.includes('AGREE');
    const btn = (
      <Button
        type="link"
        {...leftBtnProp}
        className={cx('!mb-1', leftBtnProp?.className)}
        disabled={disabled}
      >
        <span className={disabled ? '!text-content-100' : undefined}>{t('tableColumn.接受')}</span>
      </Button>
    );
    confirmUI = (
      <Popconfirm
        disabled={disabled}
        title={
          <PopConfrimContent>{t('tableColumn.确认接受买家取消订单二次确认提示')}</PopConfrimContent>
        }
        onConfirm={() => handleClick(true)}
      >
        {disabled ? <Tooltip title={t('cancel_order_btn_tooltip')}>{btn}</Tooltip> : btn}
      </Popconfirm>
    );

    if (operate?.includes('REFUSE')) {
      rejectUI = (
        <Popconfirm
          title={
            <PopConfrimContent>
              {t('tableColumn.确认拒绝买家取消订单二次确认提示')}
            </PopConfrimContent>
          }
          onConfirm={() => handleClick(false)}
        >
          <Button type="link" {...rightBtnProp} className={cx('!mb-1', rightBtnProp?.className)}>
            {t('tableColumn.拒绝')}
          </Button>
        </Popconfirm>
      );
    }
  } else if (isTiKTokUpperCase(platform)) {
    confirmUI = (
      <Button
        type="link"
        {...leftBtnProp}
        className={cx('!mb-1', leftBtnProp?.className)}
        onClick={() => handleClick(true)}
      >
        {t('tableColumn.接受')}
      </Button>
    );
    rejectUI = (
      <TikTokRefuse
        platform={platform}
        region={region}
        onRefused={onUpdate}
        internalStoreId={internalStoreId}
        externalStoreId={externalStoreId}
        externalTradeId={orderId}
        inCanceled
        btnProps={rightBtnProp}
      />
    );
  }

  return (
    <Spin spinning={loading}>
      {confirmUI}
      {rejectUI}
    </Spin>
  );
};
export default CancelApprovalBtns;
